import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MODALIDADE, type Modalidade, type SituacaoPedido } from '@arena/shared/dominio';
import {
  ROTULO_MODALIDADE_NO_FORMULARIO,
  ROTULO_ORIGEM_ALUNO,
  zConfiguracaoDoFormulario,
  type ConfiguracaoDoFormulario,
  type FormularioDaArena,
  type FormularioPublico,
  type OrigemDosAlunos,
} from '@arena/shared/schemas';
import { hojeNoFuso } from '@arena/shared/tempo';
import { ErroDeApi, mensagemDoErro } from '../api/cliente.js';
import { siteApi } from '../api/site.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import {
  dataCurta,
  dinheiro,
  paraCentavos,
  rotuloDeDuracao,
  telefoneFormatado,
} from '../agenda/formatos.js';
import { errosPorCampo, primeiraMensagem } from '../alunos/formatos.js';
import { BarrasHorizontais } from '../graficos/BarrasHorizontais.jsx';
import { Erro, EsqueletoDeCard, EsqueletoDeTabela } from '../ui/estados.jsx';
import { Botao, Campo, Card, Nota, Status, Tabela, Vazio } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import {
  diasDaFaixa,
  faixaCurta,
  horarioPedido,
  recebidoHa,
  situacaoDoPedido,
} from './formatos.js';
import { ModalDeNovoPedido } from './ModalDeNovoPedido.jsx';
import { ModalDePromocao } from './ModalDePromocao.jsx';
import { ModalDoPedido } from './ModalDoPedido.jsx';

/**
 * `#v-site` — "Reservas pedidas".
 *
 * O FORMULÁRIO PÚBLICO SAIU. O protótipo tinha um formulário embutido no
 * site da arena, com o cartão "Como o cliente vê" e o código de
 * incorporação; a arena pediu para remover o formulário e todo envio
 * automático. O que ficou é o essencial da tela: a FILA de pedidos, que a
 * recepção responde encaixando na grade, e a lista de horários livres —
 * que era a prévia do formulário e virou a ferramenta de quem atende o
 * telefone.
 *
 * O pedido entra por "Novo pedido": alguém ligou, a recepção anota.
 *
 * Ver: Arena_backend/Docs/plano/14-site-reservas-publicas.md
 */
export function PaginaDoSite() {
  const { pode } = useSessao();
  const [situacao, setSituacao] = useState<SituacaoPedido | ''>('');
  const [pedidoAberto, setPedidoAberto] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);
  const [promo, setPromo] = useState(false);

  const formulario = useQuery({ queryKey: ['site', 'formulario'], queryFn: siteApi.formulario });
  const pedidos = useQuery({
    queryKey: ['site', 'pedidos', situacao || 'todos'],
    queryFn: () => siteApi.pedidos({ ...(situacao ? { situacao } : {}), limite: 50 }),
    refetchInterval: 15_000,
  });
  const pendentes = useQuery({
    queryKey: ['site', 'pedidos', 'Pendente', 'contagem'],
    queryFn: () => siteApi.pedidos({ situacao: 'Pendente', limite: 1 }),
    refetchInterval: 15_000,
  });

  const quantosPendentes = pendentes.data?.total ?? 0;
  const hoje = pedidos.data ? hojeNoFuso(pedidos.data.fuso) : undefined;
  const podeResponder = pode('agenda', 'criar');

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Reservas pedidas</h1>
          <p>
            {quantosPendentes} {quantosPendentes === 1 ? 'pedido aguardando' : 'pedidos aguardando'}{' '}
            resposta.
          </p>
        </div>
        <div className="head-actions">
          <Botao variante="contorno" onClick={() => setPromo(true)} disabled={!hoje}>
            Criar promoção
          </Botao>
          {podeResponder && (
            <Botao icone="plus" onClick={() => setNovo(true)}>
              Novo pedido
            </Botao>
          )}
        </div>
      </div>

      <Nota>
        <b>Reservas pedidas.</b> O pedido é a anotação de uma conversa: ele entra pendente e NÃO
        ocupa a quadra. Quem tira o horário da grade é a resposta — encaixar como aula experimental
        (que cria o aluno) ou como locação avulsa. O telefone é obrigatório: sem ele não há como
        confirmar nem cobrar ausência. O produto não envia mensagem nenhuma; avisar quem pediu é da
        recepção.
      </Nota>

      <div className="grid g-1-2">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <CardDaPrevia />
          {formulario.isPending ? (
            <EsqueletoDeCard linhas={6} />
          ) : formulario.isError ? (
            <Erro
              detalhe={mensagemDoErro(formulario.error)}
              onTentarDeNovo={() => void formulario.refetch()}
            />
          ) : (
            <CardDoFormulario
              key={JSON.stringify(formulario.data.configuracao)}
              formulario={formulario.data}
            />
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Card
            titulo="Pedidos recebidos"
            dica={
              quantosPendentes > 0 ? (
                <Status situacao="atencao">
                  {quantosPendentes} {quantosPendentes === 1 ? 'pendente' : 'pendentes'}
                </Status>
              ) : undefined
            }
            acao={
              <select
                aria-label="Filtrar por situação"
                value={situacao}
                onChange={(e) => setSituacao(e.target.value as SituacaoPedido | '')}
                style={{ marginLeft: 'auto' }}
              >
                <option value="">Todos</option>
                <option value="Pendente">Pendentes</option>
                <option value="Confirmado">Confirmados</option>
                <option value="Recusado">Recusados</option>
              </select>
            }
          >
            {pedidos.isPending ? (
              <EsqueletoDeTabela linhas={5} colunas={6} />
            ) : pedidos.isError ? (
              <Erro
                detalhe={mensagemDoErro(pedidos.error)}
                onTentarDeNovo={() => void pedidos.refetch()}
              />
            ) : pedidos.data.itens.length === 0 ? (
              <Vazio>Nenhum pedido {situacao ? 'nesta situação' : 'recebido ainda'}.</Vazio>
            ) : (
              <Tabela
                colunas={['Nome', 'Telefone', 'Modalidade', 'Horário', 'Recebido', 'Situação', '']}
              >
                {pedidos.data.itens.map((p) => {
                  const agora = new Date();
                  const pilula = situacaoDoPedido(p.situacao, p.motivoRecusa);
                  return (
                    <tr key={p.id} className="row-link" onClick={() => setPedidoAberto(p.id)}>
                      <td>{p.nome}</td>
                      <td>{p.telefone ? telefoneFormatado(p.telefone) : '—'}</td>
                      <td>{ROTULO_MODALIDADE_NO_FORMULARIO[p.modalidade]}</td>
                      <td>{horarioPedido(p.inicio, agora, pedidos.data.fuso)}</td>
                      <td className="sub">{recebidoHa(p.recebidoEm, agora, pedidos.data.fuso)}</td>
                      <td>
                        <Status situacao={pilula.situacao}>{pilula.rotulo}</Status>
                      </td>
                      <td>
                        {p.situacao === 'Pendente' && (
                          <Botao pequeno onClick={() => setPedidoAberto(p.id)}>
                            Responder
                          </Botao>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </Tabela>
            )}
          </Card>

          {hoje && <CardDePromocoes hoje={hoje} />}
          {pode('alunos', 'ler') && <CardDeOrigem />}
        </div>
      </div>

      {pedidoAberto && <ModalDoPedido id={pedidoAberto} onFechar={() => setPedidoAberto(null)} />}
      {novo && <ModalDeNovoPedido onFechar={() => setNovo(false)} />}
      {promo && hoje && <ModalDePromocao hoje={hoje} onFechar={() => setPromo(false)} />}
    </>
  );
}

// ---------------------------------------------------------------------
// Os horários livres — a ferramenta de quem atende o telefone
// ---------------------------------------------------------------------

const DIAS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

function CardDaPrevia() {
  const previa = useQuery({ queryKey: ['site', 'previa'], queryFn: siteApi.previa });
  const [modalidade, setModalidade] = useState<Modalidade | ''>('');
  const [dia, setDia] = useState(0);

  if (previa.isPending) return <EsqueletoDeCard linhas={5} />;
  if (previa.isError) {
    return (
      <Erro detalhe={mensagemDoErro(previa.error)} onTentarDeNovo={() => void previa.refetch()} />
    );
  }

  const f: FormularioPublico = previa.data;
  const escolhida = modalidade || f.modalidades[0] || 'Futvolei';
  const doDia = f.dias[Math.min(dia, Math.max(0, f.dias.length - 1))];
  const soLocacao = escolhida === 'Livre';
  const horarios = (doDia?.horarios ?? []).filter((h) => !soLocacao || h.locacao);

  return (
    <Card
      titulo="Horários livres"
      dica={`${rotuloDeDuracao(f.duracaoMinutos)} · ${f.dias.length} ${f.dias.length === 1 ? 'dia' : 'dias'}`}
    >
      <p className="sub">
        A resposta a "sábado às 20h tem?". A conta é a da agenda: ocupação de agora, antecedência
        mínima, horário de funcionamento e os horários que as turmas têm reservados.
      </p>
      {f.promocoes.map((p) => (
        <div
          key={`${p.horaInicio}-${p.diasSemana.join()}`}
          className="sub"
          style={{ color: 'var(--ac-ink)' }}
        >
          {p.descontoPercentual}% na locação · {diasDaFaixa(p.diasSemana)} ·{' '}
          {faixaCurta(p.horaInicio, p.horaFim)}
        </div>
      ))}
      <div className="form f2">
        <Campo rotulo="Modalidade">
          <select value={escolhida} onChange={(e) => setModalidade(e.target.value as Modalidade)}>
            {f.modalidades.map((m) => (
              <option key={m} value={m}>
                {ROTULO_MODALIDADE_NO_FORMULARIO[m]}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Dia">
          <select value={dia} onChange={(e) => setDia(Number(e.target.value))}>
            {f.dias.map((d, i) => (
              <option key={d.data} value={i}>
                {DIAS[new Date(`${d.data}T12:00:00Z`).getUTCDay()]} · {dataCurta(d.data)}
              </option>
            ))}
          </select>
        </Campo>
      </div>
      {horarios.length === 0 ? (
        <Vazio>Nenhum horário livre neste dia{soLocacao ? ' para locação' : ''}.</Vazio>
      ) : (
        <div className="choices" style={{ marginTop: 4 }}>
          {horarios.map((h) => (
            <span key={h.inicio} className="choice" aria-disabled="true">
              {h.inicio}
              {soLocacao && h.precoLocacao !== undefined ? ` · ${dinheiro(h.precoLocacao)}` : ''}
              {h.descontoPercentual !== undefined ? ` · −${h.descontoPercentual}%` : ''}
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------
// Os parâmetros com que a arena oferece horário
// ---------------------------------------------------------------------

function CardDoFormulario({ formulario }: { formulario: FormularioDaArena }) {
  const cliente = useQueryClient();
  const avisar = useToast();
  const inicial = formulario.configuracao;

  const [modalidades, setModalidades] = useState<Modalidade[]>([...inicial.modalidades]);
  const [duracao, setDuracao] = useState(String(inicial.duracaoMinutos));
  const [diasAFrente, setDiasAFrente] = useState(String(inicial.diasAFrente));
  const [antecedencia, setAntecedencia] = useState(String(inicial.antecedenciaMinutos));
  const [prazo, setPrazo] = useState(String(inicial.prazoRespostaHoras));
  const [preco, setPreco] = useState(
    inicial.precoHoraLocacao !== null ? dinheiro(inicial.precoHoraLocacao) : '',
  );
  const [erros, setErros] = useState<Record<string, string>>({});
  const [falha, setFalha] = useState<string | null>(null);

  const salvar = useMutation({
    mutationFn: siteApi.salvarFormulario,
    onSuccess: () => void cliente.invalidateQueries({ queryKey: ['site'] }),
  });

  function alternar(m: Modalidade) {
    setModalidades((atual) => (atual.includes(m) ? atual.filter((x) => x !== m) : [...atual, m]));
  }

  async function aoSalvar() {
    setErros({});
    setFalha(null);
    const centavos = preco.trim() ? paraCentavos(preco) : null;
    if (preco.trim() && centavos === null) {
      setErros({ precoHoraLocacao: 'Valor inválido — use "100" ou "100,50".' });
      return;
    }
    const dados: ConfiguracaoDoFormulario | Record<string, unknown> = {
      // `publicado` sobrevive no contrato porque a coluna existe; não há
      // mais nada para publicar. Ver o comentário do schema.
      publicado: false,
      modalidades,
      duracaoMinutos: Number(duracao),
      diasAFrente: Number(diasAFrente),
      antecedenciaMinutos: Number(antecedencia),
      prazoRespostaHoras: Number(prazo),
      precoHoraLocacao: centavos,
    };
    const validado = zConfiguracaoDoFormulario.safeParse(dados);
    if (!validado.success) {
      setErros(errosPorCampo(validado.error.issues));
      return;
    }
    try {
      await salvar.mutateAsync(validado.data);
      avisar('Parâmetros das reservas salvos.');
    } catch (erro) {
      if (erro instanceof ErroDeApi && erro.campos) setErros(primeiraMensagem(erro.campos));
      setFalha(mensagemDoErro(erro));
    }
  }

  return (
    <Card titulo="Parâmetros das reservas" dica="o que a lista de horários livres usa">
      <Campo rotulo="Modalidades oferecidas" erro={erros['modalidades']}>
        <div className="choices">
          {MODALIDADE.map((m) => (
            <button
              key={m}
              type="button"
              className="choice"
              aria-pressed={modalidades.includes(m)}
              onClick={() => alternar(m)}
            >
              {ROTULO_MODALIDADE_NO_FORMULARIO[m]}
            </button>
          ))}
        </div>
      </Campo>
      <div className="form f2">
        <Campo rotulo="Duração do horário" erro={erros['duracaoMinutos']}>
          <select value={duracao} onChange={(e) => setDuracao(e.target.value)}>
            <option value="60">1 hora</option>
            <option value="90">1h30</option>
            <option value="120">2 horas</option>
          </select>
        </Campo>
        <Campo rotulo="Dias à frente" erro={erros['diasAFrente']}>
          <input
            type="number"
            min={1}
            max={31}
            value={diasAFrente}
            onChange={(e) => setDiasAFrente(e.target.value)}
          />
        </Campo>
        <Campo rotulo="Antecedência mínima" erro={erros['antecedenciaMinutos']}>
          <select value={antecedencia} onChange={(e) => setAntecedencia(e.target.value)}>
            <option value="0">Nenhuma</option>
            <option value="60">1 hora</option>
            <option value="120">2 horas</option>
            <option value="240">4 horas</option>
            <option value="1440">1 dia</option>
          </select>
        </Campo>
        <Campo rotulo="Prazo de resposta (horas)" erro={erros['prazoRespostaHoras']}>
          <input
            type="number"
            min={1}
            max={72}
            value={prazo}
            onChange={(e) => setPrazo(e.target.value)}
          />
        </Campo>
      </div>
      <Campo
        rotulo="Preço da hora de locação"
        erro={erros['precoHoraLocacao']}
        ajuda="Base do preço sugerido da locação; a promoção da faixa desconta dele. Em branco, o formulário não mostra preço."
      >
        <input value={preco} onChange={(e) => setPreco(e.target.value)} placeholder="R$ 0,00" />
      </Campo>
      {falha && (
        <p className="sub" style={{ color: 'var(--red-lt)' }} role="alert">
          {falha}
        </p>
      )}
      <div style={{ marginTop: 10 }}>
        <Botao pequeno onClick={() => void aoSalvar()} disabled={salvar.isPending}>
          {salvar.isPending ? 'Salvando…' : 'Salvar formulário'}
        </Botao>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------
// Promoções publicadas
// ---------------------------------------------------------------------

function CardDePromocoes({ hoje }: { hoje: string }) {
  const cliente = useQueryClient();
  const avisar = useToast();
  const promocoes = useQuery({ queryKey: ['site', 'promocoes'], queryFn: siteApi.promocoes });
  const alternar = useMutation({
    mutationFn: ({ id, ativa }: { id: string; ativa: boolean }) =>
      siteApi.alterarPromocao(id, { ativa }),
    onSuccess: (p) => {
      void cliente.invalidateQueries({ queryKey: ['site'] });
      avisar(p.ativa ? 'Promoção de volta ao formulário.' : 'Promoção tirada do formulário.');
    },
    onError: (erro) => avisar(mensagemDoErro(erro)),
  });

  const lista = promocoes.data ?? [];
  return (
    <Card titulo="Promoções" dica={`${lista.filter((p) => p.vigente).length} no formulário`}>
      {lista.length === 0 ? (
        <Vazio>Nenhuma promoção. Crie uma para a faixa ociosa que o BI apontar.</Vazio>
      ) : (
        <ul className="feed">
          {lista.map((p) => (
            <li key={p.id}>
              <div>
                <b>
                  {p.descontoPercentual}% · {diasDaFaixa(p.diasSemana)} ·{' '}
                  {faixaCurta(p.horaInicio, p.horaFim)}
                </b>
                <small>
                  até {dataCurta(p.validaAte)}
                  {p.validaAte < hoje ? ' · vencida' : ''}
                </small>
              </div>
              <div className="rt">
                <Status situacao={p.vigente ? 'ok' : 'neutra'}>
                  {p.vigente ? 'No ar' : 'Fora'}
                </Status>
                {p.validaAte >= hoje && (
                  <Botao
                    pequeno
                    variante="fantasma"
                    disabled={alternar.isPending}
                    onClick={() => alternar.mutate({ id: p.id, ativa: !p.ativa })}
                  >
                    {p.ativa ? 'Tirar' : 'Republicar'}
                  </Botao>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------
// "De onde vieram os alunos"
// ---------------------------------------------------------------------

const COR_DA_ORIGEM: Record<string, string> = {
  Indicacao: 'var(--serie-1)',
  Site: 'var(--serie-2)',
  Instagram: 'var(--serie-3)',
  PassouEmFrente: 'var(--serie-5)',
  Importacao: 'var(--serie-4)',
};

function CardDeOrigem() {
  const origem = useQuery({ queryKey: ['site', 'origem'], queryFn: siteApi.origemDosAlunos });
  return (
    <Card titulo="De onde vieram os alunos" dica="últimos 90 dias">
      {origem.isPending ? (
        <EsqueletoDeCard linhas={3} />
      ) : origem.isError ? (
        <Erro detalhe={mensagemDoErro(origem.error)} onTentarDeNovo={() => void origem.refetch()} />
      ) : origem.data.total === 0 ? (
        <Vazio>Nenhuma matrícula nos últimos 90 dias.</Vazio>
      ) : (
        <BarrasHorizontais itens={barrasDaOrigem(origem.data)} />
      )}
    </Card>
  );
}

function barrasDaOrigem(dados: OrigemDosAlunos) {
  return dados.porOrigem.map((g) => {
    const nome =
      g.origem === 'Site'
        ? 'Formulário do site'
        : g.origem
          ? ROTULO_ORIGEM_ALUNO[g.origem]
          : 'Não informado';
    const pct = Math.round((g.quantidade / dados.total) * 100);
    return {
      nome,
      valor: g.quantidade,
      rotulo: String(g.quantidade),
      cor: (g.origem && COR_DA_ORIGEM[g.origem]) || 'var(--dim)',
      dica: `${pct}% das matrículas.`,
    };
  });
}
